import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import {
  LocalShipping as DeliveryIcon,
  Store as PickupIcon,
} from "@mui/icons-material";
import useDeliveryMethods from "../../services/useDeliveryMethods";
import { IDeliveryMethod } from "../../types/seederOrders";

interface DeliveryMethodSelectorProps {
  selectedMethod: IDeliveryMethod | null;
  onSelectMethod: (method: IDeliveryMethod | null) => void;
  disabled?: boolean;
}

const DeliveryMethodSelector = ({
  selectedMethod,
  onSelectMethod,
  disabled,
}: DeliveryMethodSelectorProps) => {
  const { data, isLoading, error } = useDeliveryMethods();

  const formatCurrency = (value: string) => {
    return parseFloat(value).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  if (disabled) {
    return (
      <Alert severity="info">
        Selecione uma parceira urbana antes de escolher o método de entrega.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Erro ao carregar métodos de entrega. Tente novamente.
      </Alert>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Alert severity="warning">
        Nenhum método de entrega disponível para esta semeadora.
      </Alert>
    );
  }

  return (
    <RadioGroup
      value={selectedMethod?.id || ""}
      onChange={(e) => {
        const method = data.find((m) => m.id === parseInt(e.target.value));
        onSelectMethod(method || null);
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {data.map((method) => (
          <Card
            key={method.id}
            variant="outlined"
            sx={{
              cursor: "pointer",
              border: selectedMethod?.id === method.id ? 2 : 1,
              borderColor:
                selectedMethod?.id === method.id ? "primary.main" : "divider",
              transition: "all 0.2s",
              "&:hover": {
                borderColor: "primary.light",
                boxShadow: 1,
              },
            }}
            onClick={() => onSelectMethod(method)}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControlLabel
                  value={method.id}
                  control={<Radio />}
                  label=""
                  sx={{ m: 0 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: method.is_home_delivery
                      ? "primary.main"
                      : "secondary.main",
                  }}
                >
                  {method.is_home_delivery ? (
                    <DeliveryIcon sx={{ fontSize: 40 }} />
                  ) : (
                    <PickupIcon sx={{ fontSize: 40 }} />
                  )}
                </Box>
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6">{method.title}</Typography>
                    <Chip
                      label={method.is_home_delivery ? "Entrega" : "Retirada"}
                      size="small"
                      color={method.is_home_delivery ? "primary" : "secondary"}
                    />
                  </Box>
                  {method.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {method.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(method.fee)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    taxa de entrega
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    </RadioGroup>
  );
};

export default DeliveryMethodSelector;
